import type { CompositionPlan } from '@/types/marketplaceComposition.types';
import type { SearchResult } from '@/types/searchResponse.types';
import type { CompositionRole } from '@/types/marketplaceComposition.types';

export interface StepMetadata {
  stepNumber: number;
  role: CompositionRole;
  rationale: string;
}

export interface SelectionOutput {
  itemIds: string[];
  stepMeta: Record<string, StepMetadata>;
}

/**
 * Maps CompositionPlan steps to selectable itemIds by joining catalogItemId -> result.itemId.
 * Only includes steps that have a matching result (fail-closed - never emit a UUID on screen).
 * Returns itemIds ordered by stepNumber (Discover -> Monitor) and metadata per itemId.
 */
export function mapPlanStepsToSelection(
  plan: CompositionPlan,
  results: SearchResult[]
): SelectionOutput {
  if (!plan.steps || plan.steps.length === 0) {
    return { itemIds: [], stepMeta: {} };
  }

  // Create a lookup map of itemId -> result for efficient joining
  const resultMap = new Map(results.map((result) => [result.itemId, result]));

  // Resolve steps to itemIds, filtering out unresolvable steps
  const resolvedSteps = plan.steps
    .filter((step) => resultMap.has(step.catalogItemId))
    .map((step) => ({
      itemId: step.catalogItemId,
      stepNumber: step.stepNumber,
      role: step.role,
      rationale: step.rationale,
    }))
    .sort((a, b) => a.stepNumber - b.stepNumber); // Order by stepNumber

  // Build the output
  const itemIds = resolvedSteps.map((step) => step.itemId);
  const stepMeta: Record<string, StepMetadata> = {};

  for (const step of resolvedSteps) {
    stepMeta[step.itemId] = {
      stepNumber: step.stepNumber,
      role: step.role,
      rationale: step.rationale,
    };
  }

  return { itemIds, stepMeta };
}
